import React, { useState } from "react";

import { Code2, FileText, FileImage } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DraftsRevokeModal from "../../../common/DraftsRevokeModal/DraftsRevokeModal";
import ToolTip from "../../../common/ToolTip/ToolTip";
import {
  isHtmlTemplate,
  isLegacyTemplate,
  isSvgTemplate,
} from "../../../configs/templateTypes";
import { ReactComponent as DeleteIcon } from "../../../resources/icons/delete.svg";
import { ReactComponent as EditIcon } from "../../../resources/icons/edit.svg";
import { ReactComponent as FlyingMail } from "../../../resources/icons/flying-mail.svg";

const ProgrammeBatchCard = (props) => {
  const {
    name,
    description,
    credential_template_id,
    issuer_credential_template_id,
    navigate_url,
    deleteCertificateTemplate,
    template_type,
    programmeCode,
    isDeletable,
  } = props;

  const cardKey =
    issuer_credential_template_id ?? credential_template_id ?? null;
  const buildElementId = (prefix) =>
    cardKey ? `${prefix}-${cardKey}` : undefined;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(null);

  const toggleModal = (id) => {
    setIsModalOpen(id);
  };

  const handleDeleteConfirm = async () => {
    if (isModalOpen !== null) {
      await deleteCertificateTemplate(isModalOpen);
      setIsModalOpen(null);
    }
  };

  const handleCardKeyPress = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (credential_template_id && navigate_url) {
        localStorage.setItem("templateType", template_type);
        navigate(navigate_url);
      }
    }
  };

  const handleEditKeyPress = (
    e,
    credential_template_id,
    issuer_credential_template_id,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      navigate(
        `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`,
      );
    }
  };

  const handleIssueKeyPress = (
    e,
    credential_template_id,
    issuer_credential_template_id,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      if (isLegacyTemplate(template_type)) {
        localStorage.setItem("templateType", template_type);
        navigate(navigate_url);
      } else {
        if (!programmeCode) {
          return;
        }
        const encodedProgrammeCode = encodeURIComponent(programmeCode);
        navigate(
          `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}/${encodedProgrammeCode}`,
        );
      }
    }
  };

  const handleDeleteKeyPress = (e, issuer_credential_template_id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      toggleModal(issuer_credential_template_id);
    }
  };

  return (
    <div
      id={buildElementId("programme-batch-card")}
      className={`flex flex-col rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer bg-white border border-l-4 border-l-cfPrimaryButton h-[14rem] ${
        credential_template_id && "hover:scale-100"
      } transition-all duration-300`}
      name={credential_template_id}
      onClick={() => {
        if (credential_template_id && navigate_url) {
          localStorage.setItem("templateType", template_type);
          navigate(navigate_url);
        }
      }}
      onKeyDown={handleCardKeyPress}
      role="button"
      tabIndex={0}
    >
      <div className="p-6 flex flex-col justify-between h-full">
        <div className="flex flex-col h-full justify-between">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg pr-2 line-clamp-2">
              <span
                id={buildElementId("programme-batch-card-title")}
                className={
                  !name
                    ? "h-6 w-full animate-pulse bg-zinc-300 rounded-md block"
                    : ""
                }
              >
                {name}
              </span>
            </h3>
            {template_type && (
              <span
                id={buildElementId("programme-batch-card-status")}
                className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                  isSvgTemplate(template_type) || isHtmlTemplate(template_type)
                    ? "bg-[#EEF2FF] text-[#4F46E5]"
                    : "bg-[#ffe8cb] text-[#de881f]"
                }`}
              >
                {isHtmlTemplate(template_type) ? (
                  <div className="flex items-center gap-1">
                    <Code2 className="h-3 w-3 text-cfPrimaryButton" />
                    <span>HTML</span>
                  </div>
                ) : isSvgTemplate(template_type) ? (
                  <div className="flex items-center gap-1">
                    <FileText className="h-3 w-3 text-cfPrimaryButton" />
                    <span>SVG</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <FileImage className="h-3 w-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG" />
                    <span>Legacy</span>
                  </div>
                )}
              </span>
            )}
          </div>

          <div className="text-gray-600 text-sm line-clamp-2">
            <span
              id={buildElementId("programme-batch-card-description")}
              className={
                !description
                  ? "h-4 w-full animate-pulse bg-zinc-300 rounded-md block"
                  : ""
              }
            >
              {description}
            </span>
          </div>
        </div>

        <div>
          {isLegacyTemplate(template_type) ? (
            <div className="grid grid-cols-3 divide-x divide-slate-600 divide-solid mt-3 pt-4 border-t border-gray-200">
              <div
                id={buildElementId("programme-batch-card-edit")}
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`,
                  );
                }}
                onKeyDown={(e) =>
                  handleEditKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id,
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <EditIcon
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={t("workspace:edit_designs")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="var(--cf-primary-color)"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                id={buildElementId("programme-batch-card-issue")}
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  localStorage.setItem("templateType", template_type);
                  navigate(navigate_url);
                }}
                onKeyDown={(e) =>
                  handleIssueKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id,
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <FlyingMail
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={t("workspace:issue_certificates")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="var(--cf-primary-color)"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                id={buildElementId("programme-batch-card-delete")}
                className={`flex justify-center items-center group
    ${isDeletable === false ? "opacity-40 cursor-not-allowed pointer-events-none" : ""}
  `}
                onClick={(e) => {
                  if (isDeletable === false) return;
                  e.stopPropagation();
                  toggleModal(issuer_credential_template_id);
                }}
                onKeyDown={(e) => {
                  if (isDeletable === false) return;
                  handleDeleteKeyPress(e, issuer_credential_template_id);
                }}
                role="button"
                tabIndex={isDeletable === false ? -1 : 0}
                aria-disabled={isDeletable === false}
              >
                <ToolTip
                  component={
                    <DeleteIcon
                      className="fill-white transition-colors duration-500 hover:fill-red-500"
                      height={20}
                      width={20}
                    />
                  }
                  message={t("workspace:delete_certificates")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#DC2626"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 divide-x divide-slate-600 divide-solid mt-3 pt-4 border-t border-gray-200">
              <div
                id={buildElementId("programme-batch-card-edit-svg")}
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`,
                  );
                }}
                onKeyDown={(e) =>
                  handleEditKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id,
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <EditIcon
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={t("workspace:edit_designs")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="var(--cf-primary-color)"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                id={buildElementId("programme-batch-card-issue-svg")}
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!programmeCode) {
                    return;
                  }
                  const encodedProgrammeCode =
                    encodeURIComponent(programmeCode);
                  navigate(
                    `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}/${encodedProgrammeCode}`,
                  );
                }}
                onKeyDown={(e) =>
                  handleIssueKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id,
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <FlyingMail
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={t("workspace:issue_certificates")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="var(--cf-primary-color)"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                id={buildElementId("programme-batch-card-delete-svg")}
                className={`flex justify-center items-center group
    ${isDeletable === false ? "opacity-40 cursor-not-allowed pointer-events-none" : ""}
  `}
                onClick={(e) => {
                  if (isDeletable === false) return;
                  e.stopPropagation();
                  toggleModal(issuer_credential_template_id);
                }}
                onKeyDown={(e) => {
                  if (isDeletable === false) return;
                  handleDeleteKeyPress(e, issuer_credential_template_id);
                }}
                role="button"
                tabIndex={isDeletable === false ? -1 : 0}
                aria-disabled={isDeletable === false}
              >
                <ToolTip
                  component={
                    <DeleteIcon
                      className="fill-white transition-colors duration-500 hover:fill-red-500"
                      height={20}
                      width={20}
                    />
                  }
                  message={t("workspace:delete_certificates")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#DC2626"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <DraftsRevokeModal
        isOpen={isModalOpen !== null}
        onClose={() => setIsModalOpen(null)}
        onConfirm={handleDeleteConfirm}
        title={t("workspace:delete_certificates")}
        description={t("workspace:delete_certificates_description")}
        confirmButtonText={t("workspace:delete")}
        cancelButtonText={t("workspace:cancel")}
      />
    </div>
  );
};
export default ProgrammeBatchCard;
